/**
 * Ability Tier Logic for SEMH Strategy Eligibility
 * Maps questionnaire responses to ability tiers and provides boolean checks
 * for strategy eligibility based on communication and cognitive profiles.
 */

/**
 * Map expressive communication level to tier
 * @param {string} expressiveLevel - The expressive level from questionnaire
 * @returns {string} Tier A-F
 */
export function getExpressiveTier(expressiveLevel) {
  const tierMap = {
    'full_sentences': 'A',           // age-appropriate
    'behind_expectations': 'B',      // mild-delay
    'short_phrases': 'C',            // limited-phrases
    'minimal_words': 'D',            // very-limited
    'non_verbal': 'E',               // non-verbal-no-initiation
    'echolalia': 'F'                 // echolalia
  };
  return tierMap[expressiveLevel] || 'E'; // Default to most restrictive
}

/**
 * Map receptive communication level to tier
 * @param {string} receptiveLevel - The receptive level from questionnaire
 * @returns {string} Tier A-E
 */
export function getReceptiveTier(receptiveLevel) {
  const tierMap = {
    'multi_step': 'A',               // multi-step
    'one_to_two_step': 'B',          // one-to-two-step
    'single_step': 'C',              // single-step
    'visuals_required': 'D',         // visuals-required
    'minimal_response': 'E'          // minimal-response
  };
  return tierMap[receptiveLevel] || 'E'; // Default to most restrictive
}

/**
 * Map cognition level to tier
 * @param {string} cognitionLevel - The cognition level from questionnaire
 * @returns {string} Tier A-E
 */
export function getCognitionTier(cognitionLevel) {
  const tierMap = {
    'typical': 'A',                  // typical
    'needs_modelling': 'B',          // needs-modelling
    'high_repetition': 'C',          // high-repetition
    'concrete_only': 'D',            // concrete-only
    'brief_engagement': 'E'          // brief-engagement
  };
  return tierMap[cognitionLevel] || 'E'; // Default to most restrictive
}

/**
 * Check if child can use symbolic language (for strategies like Break Cards)
 * @param {Object} profile - Child's ability profile
 * @param {string} profile.expressiveTier - Expressive tier (A-F)
 * @param {string} profile.receptiveTier - Receptive tier (A-E)
 * @returns {boolean} True if can use symbolic language
 */
export function canUseSymbolicLanguage(profile) {
  const { expressiveTier, receptiveTier } = profile;
  
  // Expressive: age-appropriate, mild-delay, or limited-phrases
  const expressiveOK = ['A', 'B', 'C'].includes(expressiveTier);
  
  // Receptive: multi-step, one-to-two-step, or single-step
  const receptiveOK = ['A', 'B', 'C'].includes(receptiveTier);
  
  return expressiveOK && receptiveOK;
}

/**
 * Check if child can process simple reflection (for strategies like Restorative Conversation)
 * @param {Object} profile - Child's ability profile
 * @param {string} profile.receptiveTier - Receptive tier (A-E)
 * @param {string} profile.cognitionTier - Cognition tier (A-E)
 * @returns {boolean} True if can process simple reflection
 */
export function canProcessSimpleReflection(profile) {
  const { receptiveTier, cognitionTier } = profile;
  
  // Receptive: one-to-two-step or multi-step
  const receptiveOK = ['A', 'B'].includes(receptiveTier);
  
  // Cognition: typical, needs-modelling, or high-repetition
  const cognitionOK = ['A', 'B', 'C'].includes(cognitionTier);
  
  return receptiveOK && cognitionOK;
}

/**
 * Check if child can indicate feelings from choices (for Check-in/Check-out)
 * @param {Object} profile - Child's ability profile
 * @param {string} profile.expressiveTier - Expressive tier (A-F)
 * @returns {boolean} True if can indicate feelings
 */
export function canIndicateFeelings(profile) {
  const { expressiveTier } = profile;
  
  // Not non-verbal-no-initiation (tier E)
  return expressiveTier !== 'E';
}

/**
 * Check if child can imitate actions (for Mindfulness/Breathing)
 * @param {Object} profile - Child's ability profile
 * @param {string} profile.receptiveTier - Receptive tier (A-E)
 * @returns {boolean} True if can imitate actions
 */
export function canImitateActions(profile) {
  const { receptiveTier } = profile;
  
  // At least single-step receptive ability
  return ['A', 'B', 'C'].includes(receptiveTier);
}

/**
 * Check if child can tolerate small groups (for Social Skills Games)
 * @param {Object} profile - Child's ability profile
 * @param {string} profile.receptiveTier - Receptive tier (A-E)
 * @returns {boolean} True if can tolerate small groups
 */
export function canTolerateSmallGroups(profile) {
  const { receptiveTier } = profile;
  
  // At least single-step receptive ability
  return ['A', 'B', 'C'].includes(receptiveTier);
}

/**
 * Check if child can use token/reward systems
 * @param {Object} profile - Child's ability profile
 * @param {string} profile.receptiveTier - Receptive tier (A-E)
 * @param {string} profile.cognitionTier - Cognition tier (A-E)
 * @returns {boolean} True if can use token systems
 */
export function canUseTokenSystems(profile) {
  const { receptiveTier, cognitionTier } = profile;
  
  // Receptive: at least single-step
  const receptiveOK = ['A', 'B', 'C'].includes(receptiveTier);
  
  // Cognition: not brief-engagement
  const cognitionOK = cognitionTier !== 'E';
  
  return receptiveOK && cognitionOK;
}

/**
 * Create ability profile from questionnaire responses
 * @param {Object} responses - Questionnaire responses
 * @returns {Object} Ability profile with tiers and boolean checks
 */
export function createAbilityProfile(responses) {
  const expressiveTier = getExpressiveTier(responses.comm_primary);
  const receptiveTier = getReceptiveTier(responses.comm_receptive);
  const cognitionTier = getCognitionTier(responses.cog_learning);
  
  const profile = {
    expressiveTier,
    receptiveTier,
    cognitionTier,
    regulationIndependence: responses.semh_regulation
  };
  
  // Add boolean checks
  profile.canUseSymbolicLanguage = canUseSymbolicLanguage(profile);
  profile.canProcessSimpleReflection = canProcessSimpleReflection(profile);
  profile.canIndicateFeelings = canIndicateFeelings(profile);
  profile.canImitateActions = canImitateActions(profile);
  profile.canTolerateSmallGroups = canTolerateSmallGroups(profile);
  profile.canUseTokenSystems = canUseTokenSystems(profile);
  
  return profile;
}
